import React, { FC } from 'react';
import { find, isEmpty, get } from 'lodash';
import PropTypes from 'prop-types';
import { DescriptionLabel } from '@src/components/DescriptionLabel';
import { MatchingCandidates } from '@src/containers/MatchingCandidates';
import { EMPLOYMENT_TYPES } from '@src/constants';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { idify } from '@src/util/idify';
import { SalaryPeriodEnum, useGetOpeningReachQuery, useGetSalaryPeriodsQuery } from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import './styles.scss';

export const SalaryReview: FC = ({ opening }) => {
  const dispatch = useAppDispatch();

  const {
    error,
    loading: isLoading,
    data,
  } = useGetOpeningReachQuery({
    onError: () => dispatch(setErrorFlashMessage()),
    variables: { id: idify(opening) },
  });

  const { data: salaryPeriodsData } = useGetSalaryPeriodsQuery();

  const salaryPeriods = salaryPeriodsData?.salaryPeriods;

  const { targetLocations, isRemote, isRelocation, salaryRange, workload, employmentType } = opening;

  const { local, global, remote } = get(data, 'opening.reach', {});
  const reachAmount = !isLoading
    ? isRelocation || isEmpty(targetLocations)
      ? global
      : isRemote
      ? remote
      : local
    : undefined;

  const employmentTypeLabel = find(EMPLOYMENT_TYPES, ({ key }) => key === employmentType).name;

  const getSalaryPeriodLabel = (period: SalaryPeriodEnum): string | undefined => {
    const salaryPeriod = find(salaryPeriods, ({ value }) => value === period);

    return salaryPeriod?.label;
  };

  return (
    <>
      <div className="salary-review">
        <DescriptionLabel
          className="salary-review__left"
          description={
            employmentTypeLabel +
            (employmentType === 'part-time' || employmentType === 'PART_TIME'
              ? `, ${workload.hours} hours${workload.period === 'TOTAL' ? '' : ' per ' + workload.period}`
              : '')
          }
          label="Employment type"
        />
      </div>

      <div className="salary-review">
        {salaryRange ? (
          <div className="salary-review__left">
            <h3 className="salary-review__budget-value">
              {salaryRange.from} – {salaryRange.to} {get(salaryRange, 'currency.code', '')} /{' '}
              {getSalaryPeriodLabel(salaryRange.period)}
            </h3>

            <div className="salary-review__budget-label">Target group gross salary (before tax)</div>
          </div>
        ) : null}

        {!error ? (
          <MatchingCandidates
            label={isRelocation ? 'Targeting globally' : 'Targeting locally'}
            matchesCount={reachAmount}
            oldMatchesCount={0}
          />
        ) : null}
      </div>
    </>
  );
};

SalaryReview.propTypes = {
  opening: PropTypes.shape({
    employmentType: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    isRelocation: PropTypes.bool,
    isRemote: PropTypes.bool,
    salaryRange: PropTypes.shape({
      currency: PropTypes.oneOfType([
        PropTypes.shape({
          code: PropTypes.string.isRequired,
          id: PropTypes.string.isRequired,
        }),
      ]).isRequired,
      from: PropTypes.number.isRequired,
      period: PropTypes.string.isRequired,
      to: PropTypes.number.isRequired,
    }).isRequired,
    targetLocations: PropTypes.array,
    workload: PropTypes.shape({
      hours: PropTypes.number,
      period: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  setErrorFlashMessage: PropTypes.func.isRequired,
};
